import { WORLD_PACKAGE_LIMITS } from './format.js';
/**
 * A world's picture (ADR-059): what the store, Les maken's place cards and the library show of a
 * world, and what a world must have, checked, before it is published. Its shape is the format's
 * (ADR-050: 16:9, 640 to 1920 px wide); its look is measured by the media worker (`POST /look`: the
 * picture sampled at 160 × 90, in luma 0–255) and judged here, so the API, the backfill and the
 * worker's own test of the console's place pictures hold one set of numbers.
 *
 * The thresholds were set on the console's 14 place pictures (apps/console/web/plaatsen, all pass;
 * the tightest are the studio's spread 11.1 and edges 0.48 %, the solar system's 95th percentile
 * 42.6 and the science lab's 5th percentile 184.3) and on synthetic pictures that must fail: black,
 * white and grey (spread 0), faint noise on black (spread 0.5), a full-width gradient (edges 0).
 */
export const WORLD_PICTURE_RULES = {
    minWidth: WORLD_PACKAGE_LIMITS.picture.minWidth,
    maxWidth: WORLD_PACKAGE_LIMITS.picture.maxWidth,
    /** Bytes as stored: what is uploaded, measured and served at most. */
    maxBytes: WORLD_PACKAGE_LIMITS.picture.maxBytes,
    /** 16:9 as the format checks it: |width × 9 − height × 16| at most this. */
    aspectSlack: 16,
    /** Below this standard deviation of the luma, the picture is one colour. */
    minStdev: 6,
    /** Below this share (per cent) of neighbouring pairs differing by more than 8 levels: no shapes. */
    minEdges: 0.2,
    /** When even the 95th percentile is below this, the picture is too dark. */
    darkP95: 30,
    /** When even the 5th percentile is above this, the picture is too bright. */
    brightP5: 235,
};
/** What publishing a world without any picture is refused with. */
export const WORLD_PICTURE_MISSING = 'Deze wereld heeft nog geen plaatje. Maak er een met "Maak plaatje" in de wereldkit, of kies ' +
    '"Plaatje vervangen" op de pagina van de wereld.';
/** What a picture that cannot be read is refused with. */
export const WORLD_PICTURE_UNREADABLE = 'Het plaatje kan niet worden gelezen: het moet een JPEG of PNG zijn.';
/** What a picture over `WORLD_PICTURE_RULES.maxBytes` is refused with. */
export const WORLD_PICTURE_TOO_LARGE = 'Het plaatje is groter dan 3 MB. Bewaar het kleiner (bijvoorbeeld 1600 × 900 px als JPEG) en probeer het opnieuw.';
/** Why a picture of `width` × `height` px is the wrong shape or size, in Dutch; null when it is right. */
export function worldPictureSizeProblem(width, height) {
    const rules = WORLD_PICTURE_RULES;
    const right = width >= rules.minWidth &&
        width <= rules.maxWidth &&
        Math.abs(width * 9 - height * 16) <= rules.aspectSlack;
    return right
        ? null
        : `Het plaatje moet 16:9 zijn en ${rules.minWidth} tot ${rules.maxWidth} px breed ` +
            `(het is ${width} × ${height} px).`;
}
/** Why a picture that looks like `look` shows nothing, in Dutch; empty when it is fine. */
export function worldPictureLookProblems(look) {
    const rules = WORLD_PICTURE_RULES;
    // One colour is the whole story: it is also without edges, and maybe dark or bright.
    if (look.stdev < rules.minStdev) {
        return ['Het plaatje is (bijna) één kleur: er is niets op te zien.'];
    }
    const problems = [];
    if (look.edges < rules.minEdges) {
        problems.push('Op het plaatje is niets te zien: alleen een egale kleurovergang, zonder randen of vormen.');
    }
    if (look.p95 < rules.darkP95) {
        problems.push('Het plaatje is te donker: zelfs het lichtste deel is bijna zwart.');
    }
    if (look.p5 > rules.brightP5) {
        problems.push('Het plaatje is te licht: zelfs het donkerste deel is bijna wit.');
    }
    return problems;
}
/** The size the media worker samples a picture at (`POST /look`): 16:9, small enough to be quick, large enough for edges. */
export const LOOK_SAMPLE = { width: 160, height: 90 };
/** A neighbouring pair that differs by more than this many levels (0–255) is an edge. */
export const EDGE_LEVELS = 8;
const round = (value, places) => {
    const factor = 10 ** places;
    return Math.round(value * factor) / factor;
};
/**
 * The look of `luma` (row by row, `width` × `height`, values 0–255), as the media worker measures it
 * (its look.ts holds a copy, services/media-worker/src/world/copies.test.ts): the kit measures a
 * picture with it too (ADR-099).
 */
export function lookOfLuma(luma, width, height) {
    const count = width * height;
    let sum = 0;
    for (let i = 0; i < count; i++)
        sum += luma[i];
    const mean = sum / count;
    let squares = 0;
    for (let i = 0; i < count; i++)
        squares += (luma[i] - mean) ** 2;
    const sorted = Array.from({ length: count }, (_, i) => luma[i]).sort((a, b) => a - b);
    const at = (q) => sorted[Math.floor(q * (count - 1))];
    let pairs = 0;
    let edges = 0;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const here = luma[y * width + x];
            if (x + 1 < width) {
                pairs++;
                if (Math.abs(luma[y * width + x + 1] - here) > EDGE_LEVELS)
                    edges++;
            }
            if (y + 1 < height) {
                pairs++;
                if (Math.abs(luma[(y + 1) * width + x] - here) > EDGE_LEVELS)
                    edges++;
            }
        }
    }
    return {
        mean: round(mean, 1),
        stdev: round(Math.sqrt(squares / count), 1),
        p5: round(at(0.05), 1),
        p95: round(at(0.95), 1),
        edges: pairs === 0 ? 0 : round((edges / pairs) * 100, 2),
    };
}
//# sourceMappingURL=picture.js.map